/** The hash router over the ten areas (U001; UI spec §1). */
import type { Scope } from "./dom.ts";

export const AREAS = [
  "today",
  "opportunities",
  "watchlists",
  "portfolio",
  "orders",
  "research",
  "data",
  "operations",
  "security",
  "settings",
] as const;

export type Area = (typeof AREAS)[number];

export const AREA_LABELS: Readonly<Record<Area, string>> = {
  today: "Today",
  opportunities: "Opportunities",
  watchlists: "Watchlists",
  portfolio: "Portfolio",
  orders: "Orders",
  research: "Research",
  data: "Data",
  operations: "Operations",
  security: "Security",
  settings: "Settings",
};

export interface Route {
  readonly area: Area;
  readonly sub: string | null;
  readonly param: string | null;
  readonly query: Readonly<Record<string, string>>;
}

export type Parsed =
  | { readonly kind: "route"; readonly route: Route }
  | { readonly kind: "unknown"; readonly hash: string };

/** Parse `#/area[/sub[/param]][?k=v&...]`; an empty hash is Today, an unknown area fails closed. */
export function parseRoute(hash: string): Parsed {
  const text = hash.startsWith("#") ? hash.slice(1) : hash;
  const [path = "", search = ""] = text.split("?", 2);
  const segments = path.split("/").filter((segment) => segment !== "");
  const [first, sub, param] = segments;
  const area = first ?? "today";
  if (!isArea(area) || segments.length > 3) {
    return { kind: "unknown", hash };
  }
  const query: Record<string, string> = {};
  for (const [key, value] of new URLSearchParams(search)) {
    query[key] = value;
  }
  const decoded = decode(param);
  if (decoded === undefined) {
    return { kind: "unknown", hash };
  }
  return { kind: "route", route: { area, sub: sub ?? null, param: decoded, query } };
}

function decode(param: string | undefined): string | null | undefined {
  if (param === undefined) {
    return null;
  }
  try {
    return decodeURIComponent(param);
  } catch {
    return undefined;
  }
}

function isArea(value: string): value is Area {
  return (AREAS as readonly string[]).includes(value);
}

/** Build the hash of a route. */
export function hashOf(
  area: Area,
  sub?: string,
  param?: string,
  query: Readonly<Record<string, string>> = {},
): string {
  const path = [area, sub, param === undefined ? undefined : encodeURIComponent(param)]
    .filter((part): part is string => part !== undefined)
    .join("/");
  const search = new URLSearchParams(query).toString();
  return `#/${path}${search === "" ? "" : `?${search}`}`;
}

/** Call `onRoute` now and on every hash change; the listener is released with the scope. */
export function startRouter(win: Window, scope: Scope, onRoute: (parsed: Parsed) => void): void {
  const run = (): void => {
    onRoute(parseRoute(win.location.hash));
  };
  scope.listen(win, "hashchange", run);
  run();
}
